import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const COLORS = ["#1F5AFF", "#0C0A09", "#16A34A", "#2563EB", "#F59E0B", "#DC2626", "#9333EA"];

const tooltipStyle = {
  backgroundColor: "#0C0A09",
  border: "1px solid #0C0A09",
  color: "#fff",
  fontSize: 12,
  fontFamily: "'JetBrains Mono', monospace",
};

export function ChartCard({ title, subtitle, height = 260, children, testId, accent }) {
  return (
    <div
      className={`grid-surface p-5 flex flex-col ${accent ? "border-l-4 border-l-brand-500" : ""}`}
      data-testid={testId}
    >
      <div className="overline text-stone-500">{title}</div>
      {subtitle && <div className="text-sm text-stone-600 mt-1">{subtitle}</div>}
      <div className="mt-4 flex-1" style={{ minHeight: height }}>
        <ResponsiveContainer width="100%" height={height}>
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function BarSeries({ data, xKey = "month", yKey = "amount", yLabel = "Amount" }) {
  return (
    <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      <CartesianGrid stroke="#E7E5E4" strokeDasharray="2 4" vertical={false} />
      <XAxis dataKey={xKey} stroke="#78716C" fontSize={11} tickLine={false} />
      <YAxis stroke="#78716C" fontSize={11} tickLine={false} />
      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(249,115,22,0.08)" }} />
      <Bar dataKey={yKey} fill="#1F5AFF" name={yLabel} radius={[0, 0, 0, 0]} maxBarSize={56} />
    </BarChart>
  );
}

export function LineSeries({ data, xKey = "month", yKey = "count", yLabel = "Count" }) {
  return (
    <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      <CartesianGrid stroke="#E7E5E4" strokeDasharray="2 4" vertical={false} />
      <XAxis dataKey={xKey} stroke="#78716C" fontSize={11} tickLine={false} />
      <YAxis stroke="#78716C" fontSize={11} tickLine={false} />
      <Tooltip contentStyle={tooltipStyle} />
      <Line
        type="monotone"
        dataKey={yKey}
        stroke="#0C0A09"
        strokeWidth={2}
        dot={{ stroke: "#1F5AFF", strokeWidth: 2, fill: "#fff", r: 4 }}
        name={yLabel}
      />
    </LineChart>
  );
}

export function PieSeries({ data, nameKey = "status", valueKey = "count" }) {
  return (
    <PieChart>
      <Pie
        data={data}
        dataKey={valueKey}
        nameKey={nameKey}
        cx="50%"
        cy="50%"
        innerRadius={50}
        outerRadius={90}
        paddingAngle={1}
      >
        {data.map((_, i) => (
          <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth={2} />
        ))}
      </Pie>
      <Tooltip contentStyle={tooltipStyle} />
      <Legend
        verticalAlign="bottom"
        height={30}
        iconType="square"
        wrapperStyle={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}
      />
    </PieChart>
  );
}

export function EmptyChart({ label = "No data yet" }) {
  return (
    <div className="flex items-center justify-center h-full text-stone-400 overline text-xs">
      {label}
    </div>
  );
}
